import { AnimatePresence, motion } from "motion/react"
import { useTranslation } from "react-i18next"

import type { GroupedActivityMessage } from "../../types/activity-message-types"
import type { ToolApproval } from "@/api/models"

import { translateActivityGroupTitle } from "@/utils/activity-message-translator"

import { ActivityMessageItem } from "./activity-message-item"

type ActivityMessageGroupProps = {
  group: GroupedActivityMessage
  groupIndex: number
  citationSection?: string
  isLast?: boolean
  demoMode?: boolean
  toolApprovals?: ToolApproval[]
}

export function ActivityMessageGroup({
  group,
  groupIndex,
  citationSection,
  isLast = false,
  demoMode = false,
  toolApprovals,
}: ActivityMessageGroupProps) {
  const { t } = useTranslation()
  const translatedTitle =
    group.activities.length > 0
      ? translateActivityGroupTitle(group.activities[0]?.details?.group_title, t)
      : undefined
  const displayTitle = translatedTitle || group.title

  return (
    <motion.section
      animate={{ opacity: 1, y: 0 }}
      className="relative flex flex-col gap-4"
      id={`group-${groupIndex}`}
      initial={{ opacity: 0, y: 20 }}
      transition={{
        duration: 0.5,
        delay: groupIndex * 0.1, // stagger animation for multiple groups
        ease: [0.25, 0.1, 0.25, 1],
      }}
    >
      {!isLast && (
        <div className="bg-primary/10 absolute top-6 left-3 h-full w-0.5 -translate-x-1/2" />
      )}
      <div className="flex min-w-0 items-center gap-3">
        <div className="relative flex shrink-0">
          <div className="border-foreground bg-primary relative z-10 size-6 rounded-full border">
            <span className="text-primary-foreground absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-xs font-semibold">
              {groupIndex + 1}
            </span>
          </div>
        </div>
        <h2 className="text-xl">{displayTitle}</h2>
      </div>
      <div className="ml-10 flex flex-col gap-4">
        <AnimatePresence>
          {group.activities.map((activity, activityIndex) => (
            <ActivityMessageItem
              key={activityIndex}
              activity={activity}
              activityIndex={activityIndex}
              citationSection={citationSection}
              demoMode={demoMode}
              toolApprovals={toolApprovals}
            />
          ))}
        </AnimatePresence>
      </div>
    </motion.section>
  )
}
